import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import ConfirmModal from "@elements/modals/ConfirmModal";
import { IoTrashOutline } from "react-icons/io5";
import { apiMessage } from "@/utils/apiMessage";

type LookupEntry = { key: number | string; value: string };
type AccountingLookups = {
    glAccounts?: LookupEntry[];
    paymentIntervals?: LookupEntry[];
};

type FeeScheduleItem = {
    id?: number;
    itemName?: string;
    amount?: number;
    notes?: string;
    glAccountId?: number;
    interval?: string;
    [key: string]: unknown;
};

type FeeScheduleItemModalProps = {
    isOpen: boolean;
    item: FeeScheduleItem | null;
    feeScheduleId?: number | string;
    onClose?: () => void;
    updateSpinner?: (isLoading: boolean) => void;
    lookups?: AccountingLookups;
};

export default function FeeScheduleItemModal(props: FeeScheduleItemModalProps) {
    const { isOpen, item, feeScheduleId, onClose, updateSpinner, lookups } = props;
    const [form, setForm] = useState<FeeScheduleItem | null>(item);
    const [existingItem] = useState<FeeScheduleItem | null>(item);
    const [changed, setChanged] = useState(false);
    const [accountingLookups, setLookups] = useState<AccountingLookups | undefined>(lookups);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (isOpen) {
            getLookups();
            // Generic shallow comparison of form and existingItem
            const a = form || {};
            const b = existingItem || {};
            const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
            const isDifferent = Array.from(allKeys).some(key => (a[key] || "") !== (b[key] || ""));
            setChanged(isDifferent);
        }
    }, [form, existingItem]);

    useEffect(() => {
        if(lookups) 
            setLookups(lookups);
    }, [lookups]);

    useEffect(() => {
        if (isOpen) {
            getLookups();
            setForm(item);
        }
        else
            setForm(null);
    }, [isOpen, item]);

    if (!isOpen || !form) return null;
    async function onDelete() {
        setShowDeleteConfirm(false);
        if (!item || !item.id) return;
        updateSpinner && updateSpinner(true);
        try {
            const resp = await RightTimeApi.deleteAsync(`/FeeScheduleItem/${item.id}`);
            if (resp && resp.ok) {
                onClose && onClose();
            } else {
                toast.error("Failed to delete fee schedule item");
            }
        } catch (err) {
            toast.error("Error deleting fee schedule item: " + err.message);
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }


    function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        const { name, value, type } = e.target;
        const checked = type === "checkbox" && "checked" in e.target ? e.target.checked : false;
        let usedValue: string | number | boolean = value;
        if (type === "number")
            usedValue = parseFloat(value) || 0; // Ensure number fields are parsed correctly
        if (type === "select-one")
            usedValue = value ? parseInt(value) : 0; // Ensure select values are parsed correctly
        setForm(f => ({
            ...f,
            [name]: type === "checkbox" ? checked : usedValue,
        } as FeeScheduleItem));
    }

    async function getLookups() {
        if (isOpen && !accountingLookups) {
            let resp = await RightTimeApi.getAsync("/AccountingLookups");
            if (resp && resp.ok && resp.jsonData) {
                setLookups(resp.jsonData);
            }
        }
    }


    async function handleSubmit() {
        updateSpinner && updateSpinner(true);
        try {
            const merged = { ...item, ...form, feeScheduleId: feeScheduleId };
            const payload: Record<string, unknown> = {};
            Object.keys(merged).forEach(key => {
                const val = merged[key as keyof typeof merged];
                if (val !== undefined && val !== null && val !== "") {
                    payload[key] = val;
                }
            });
            const resp = await RightTimeApi.sendAsync("FeeScheduleItem", payload);
            if (resp && resp.ok) {
                onClose && onClose();
            } else {
                const failMsg = apiMessage(resp);
                if (failMsg)
                    toast.error("Failed to save fee schedule item: " + failMsg);
                else
                    toast.error("Failed to save fee schedule item");
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            toast.error("Error saving fee schedule item: " + message);
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }


    const fields: Record<string, ReactNode> = {
        "Item Name": <input name="itemName" value={form?.itemName || ""} onChange={handleChange} required />,
        "Amount": <input name="amount" type="number" value={form?.amount || ""} onChange={handleChange} min="0" step="0.01" />,
        "Notes": <input name="notes" value={form?.notes || ""} onChange={handleChange} />,
        "GL Account": (
            <select
                name="glAccountId"
                value={form.glAccountId}
                onChange={handleChange}
                required
            >
                <option value="" disabled>Select category</option>
                {accountingLookups?.glAccounts && accountingLookups.glAccounts.map(acct => (
                    <option key={acct.key} value={acct.key}>{acct.value}</option>
                ))}
            </select>
        ),
        "Interval": (
            <select
                name="interval"
                value={form?.interval || ""}
                onChange={handleChange}
                required
            >
                <option value="" disabled>Select interval</option>
                {accountingLookups?.paymentIntervals && accountingLookups.paymentIntervals.map(interval => (
                    <option key={interval.key} value={interval.value}>{interval.value}</option>
                ))}
            </select>
        ),
    };

    const footer = (
        <>
            <div className="footerLeft">
                <button
                    type="button"
                    className={`btn btn-danger iconButton${!item || !item.id ? ' hidden' : ''}`}
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete Fee Schedule Item"
                    aria-label="Delete Fee Schedule Item"
                    disabled={!item || !item.id}
                ><IoTrashOutline /></button>
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={!changed}
                >
                    Save
                </button>
            </div>
        </>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                title={item && item.id ? "Edit Fee Schedule Item" : "Add Fee Schedule Item"}
                onClose={onClose}
                footer={footer}
                className="feeScheduleItemModal"
            >
                <ModalFormFields fields={fields} />
            </Modal>
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={onDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Fee Schedule Item"
                message={`Are you sure you want to delete the item${item?.itemName ? ` "${item.itemName}"` : ''}?`}
                confirmText="Yes, Delete Item"
                dangerText="This will permanently remove the fee schedule item and all associated data."
            />
        </>
    );
}


